import { useEffect, useMemo, useRef, useState } from 'react';
import { D, type Dec, fiscalDate, formatMoney } from '@makila/fiscal';
import {
  applyGlobalDiscount, applyLoyalty, applyPromotions, billableItems, hasDiscount, cartTotals, emitProforma, emitReceipt, emitSale, emitVoucher, newVoucherCode, duePeriods, pendingCharges, periodLabel, rentalLine, type Contract, saveOrder, usePackageSession, lotProblem, requestStock, createReservation, type ReservationItem, type Reservation, parseGs1, parseScaleBarcode, scaleQuantity, tierPrice, warrantyEnd,
  type CartLine, type CashReport, type CatalogItem, type Customer, type IssuedDocument, type LicenceStatus, type Order, type CreditAccount, type CustomerPackage, type Giftcard, type LotInfo, type Prescription, type PriceTier, type PromoLine, type Promotion, type Signer,
} from '@makila/pos-core';
import { Beer, Bookmark, ClipboardList, CalendarDays, DoorOpen, FileText, Gift, LayoutDashboard, LayoutGrid, ListOrdered, Minus, Plug, Moon, Percent, Plus, Printer, RefreshCw, Search, ShoppingCart, Sun, Ticket, Trash2, Truck, Undo2, User, Wallet, X } from 'lucide-react';
import clsx from 'clsx';
import { CashPanel } from '../components/CashPanel';
import { PanelItem, Rail, RailItem, RailTool, SidePanel } from '../components/Rail';
import { CustomerDialog } from '../components/CustomerDialog';
import { DiscountDialog } from '../components/DiscountDialog';
import { ReturnDialog } from '../components/ReturnDialog';
import { PayDialog } from '../components/PayDialog';
import { AgeDialog, PriceDialog, SerialDialog, type StoreStock, VariantDialog, WeightDialog } from '../components/RetailDialogs';
import { CreditSaleDialog, ReceiptAmountDialog } from '../components/CreditDialogs';
import { VoucherDialog } from '../components/VoucherDialog';
import { ReservationsDialog, ReserveDialog } from '../components/ReservationDialogs';
import { type Staff, StaffAvatar, StaffPicker, staffPrice } from '../components/StaffPicker';
import { PrescriptionDialog, SubstituteDialog } from '../components/PharmacyDialogs';
import type { Operator } from './Lock';
import { StatusPill } from '../components/StatusPill';
import { type DeviceState, hasModule, identity, terminalApi } from '../lib/device';
import type { IdbTerminalStore } from '../lib/idb-store';
import { receiptHtml } from '../lib/receipt';
import { printThermal } from '../lib/hw/printer';
import { HardwareDialog } from '../components/HardwareDialog';
import type { SyncStatus } from '../lib/sync';
import { useUpdateReady } from '../lib/update';
import { useMedia } from '../lib/media';
import { useTheme } from '../lib/theme';

export interface Product {
  id: string; categoryId: string | null; code: string; name: string; barcode: string | null; unit: string;
  unitPrice: string; priceIncludesTax: boolean; taxRate: string; exemptionCode: string | null; active: boolean;
  soldByWeight?: boolean; scalePlu?: string | null; priceTiers?: PriceTier[]; variantOf?: string | null; variantLabel?: string | null;
  /** Moda (0033): célula da variante; no modelo, a grelha. Colecção (saldos). */
  variantSize?: string | null; variantColor?: string | null; variantSizes?: string[]; variantColors?: string[]; collection?: string | null;
  trackLots?: boolean; activeIngredient?: string | null; requiresPrescription?: boolean; controlled?: boolean; regulatedPrice?: boolean;
  // Ficha completa (0020); ausentes num catálogo antigo = valores por omissão.
  kind?: 'product' | 'service' | 'kit' | 'container'; image?: string | null; shortName?: string | null; brand?: string | null; tags?: string[]; extraBarcodes?: string[];
  openPrice?: boolean; noDiscount?: boolean; ageRestricted?: boolean; posVisible?: boolean; buttonColor?: string | null; durationMin?: number | null;
  /** Entrada (0029): pessoas por unidade; bilhete com QR; noite do evento. */
  admits?: number | null; ticket?: boolean; eventDate?: string | null;
  /** Números de série (0020/0036): pedido em cada unidade; garantia em meses. */
  serialRequired?: boolean; warrantyMonths?: number | null;
}
interface Category { id: string; name: string; color: string | null; sortOrder: number; active: boolean }
/** Linha do carrinho. Cada pesagem é uma linha; os outros artigos juntam-se na mesma. */
interface Line { key: string; product: Product; qty: string; discountPct: string; lot?: LotInfo; /** Preço escrito na caixa (artigo de preço livre) ou o do profissional. */ price?: string; /** Quem fez o serviço (comissões). */ staffId?: string; /** Mensalidade de um contrato de aluguer (um mês por linha). */ rental?: { contractId: string; period: string; label: string }; /** Sessão de um pacote do cliente (sai do documento: só abate o saldo). */ pkgId?: string; /** Número de série da unidade (uma por linha) e o fim da garantia. */ serial?: string; warrantyUntil?: string | null; subst?: { productId: string; description: string; prescriberConsent: boolean } }

/** Preço da linha: o do escalão de quantidade atingido (preços por quantidade), senão o normal. */
/** Hoje em Maputo (validades). */
const today = () => fiscalDate();

const linePrice = (l: Line) => l.price ?? tierPrice(l.product.unitPrice, l.product.priceTiers, l.qty);

const asProduct = (i: CatalogItem) => i.data as unknown as Product;
const asCategory = (i: CatalogItem) => i.data as unknown as Category;

export function SaleScreen(p: {
  store: IdbTerminalStore; device: DeviceState; signer: Signer; licence: LicenceStatus | null; sync: SyncStatus | null;
  operator: Operator; cashSessionId: string;
  onSold: () => void; onSyncNow: () => void; onLock: () => void; onCashChanged: () => void; onSessionClosed: (z: CashReport) => void;
  /** Restaurante: voltar ao mapa de mesas. */
  onTables?: () => void;
  /** Bar: o ecrã inicial chama-se "Contas". */
  tablesLabel?: string;
  /** Gerente: abre (ou traz para a frente) a gestão. */
  onGestao?: () => void;
  /** Vindo do mapa de mesas: abre logo o painel da caixa ou a devolução. */
  openOnStart?: 'cash' | 'return' | null;
  /** Fila de espera: voltar à fila; e a senha que se está a cobrar (os serviços e quem os fez). */
  onQueue?: () => void;
  onAgenda?: () => void;
  /** Entradas (ginásio): voltar; e o sócio que vem renovar. */
  onAccess?: () => void;
  /** Bar com entradas: o ecrã da porta (bilhetes, convidados, contador). */
  onDoor?: () => void;
  /** Serviços: o quadro das ordens de serviço. */
  onWorkOrders?: () => void;
  initialCustomer?: Customer | null;
  fromOrder?: Order | null;
  /** Entregas: voltar à lista do dia; o que o cliente leva habitualmente; e a venda emitida (fecha a paragem). */
  onDeliveries?: () => void;
  initialItems?: { productId: string; quantity: string }[];
  onIssued?: (doc: IssuedDocument) => void;
}) {
  const [cashOpen, setCashOpen] = useState(p.openOnStart === 'cash');
  const applyUpdate = useUpdateReady();
  const [operators, setOperators] = useState<Map<string, string>>(new Map());
  useEffect(() => { void p.store.catalog('operator').then((i) => setOperators(new Map(i.map((o) => [o.id, String(o.data.name)])))); }, [p.store]);
  const [products, setProducts] = useState<Product[]>([]);
  const [promos, setPromos] = useState<Promotion[]>([]);
  const [lots, setLots] = useState<(LotInfo & { productId: string })[]>([]);
  // As promoções dependem da hora (hora feliz): reavaliam-se a cada minuto.
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setClock(Date.now()), 60_000); return () => clearInterval(t); }, []);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cat, setCat] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [cart, setCart] = useState<Line[]>([]);
  const [paying, setPaying] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(p.initialCustomer ?? null);
  const [pickCustomer, setPickCustomer] = useState(false);
  // Fidelização: saldo do cliente (o último que a caixa conhece) e se os usa nesta venda.
  const [custPoints, setCustPoints] = useState(0);
  // Profissionais (módulo): quem atende esta venda (vale para as linhas novas) e a escolha por linha.
  const [staff, setStaff] = useState<Staff[]>([]);
  const [saleStaff, setSaleStaff] = useState<string | null>(null);
  const [pickStaff, setPickStaff] = useState<{ line?: string } | null>(null);
  useEffect(() => {
    if (!hasModule(p.device, 'staff')) { setStaff([]); return; }
    void p.store.catalog('operator').then((all) => {
      const list = all.map((i) => i.data as unknown as Staff & { sortOrder?: number }).filter((o) => o.active && o.performsServices)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'pt'));
      setStaff(list);
      setSaleStaff((cur) => cur ?? (list.some((o) => o.id === p.operator.id) ? p.operator.id : null));
    });
  }, [p.store, p.device, p.operator.id, p.sync?.lastSyncAt]);
  const staffNames = useMemo(() => new Map(staff.map((x) => [x.id, x.name])), [staff]);
  /** A linha com outro profissional: o preço passa a ser o dele (se tiver um próprio). */
  const withStaff = (l: Line, staffId: string | null): Line => {
    const own = staffId ? staffPrice(staff.find((x) => x.id === staffId), l.product.id)?.price : null;
    const { staffId: _s, ...rest } = l;
    const price = l.product.openPrice ? l.price : own ?? undefined;
    const { price: _p, ...base } = rest;
    return { ...base, ...(price ? { price } : {}), ...(staffId ? { staffId } : {}) };
  };
  // Conta corrente do cliente escolhido (a última que a caixa conhece) e os diálogos de crédito e recibo.
  const [account, setAccount] = useState<CreditAccount | null>(null);
  // Pacotes do cliente (decisão F3) e o aviso de sessões registadas sem documento.
  const [packages, setPackages] = useState<CustomerPackage[]>([]);
  const [info, setInfo] = useState<string | null>(null);
  // Vales-oferta (decisão F2): venda do vale (valor → pagamento) e procura do vale no pagamento.
  const [selling, setSelling] = useState<'form' | { amount: string; validityDays: number | null; buyer: string } | null>(null);
  // Reservas com sinal (decisão F4): o carrinho guardado com sinal; a lista das reservas da loja.
  const [reserving, setReserving] = useState<'form' | { customer: { id?: string; name: string; nuit: string; phone: string | null }; dueDate: string; deposit: string } | null>(null);
  const [reservationsOpen, setReservationsOpen] = useState(false);
  const [openReservations, setOpenReservations] = useState(0);
  const voucherLookup = hasModule(p.device, 'giftcards')
    ? async (code: string) => (await p.store.catalog('giftcard')).map((i) => i.data as unknown as Giftcard).find((g) => g.code === code)
    : undefined;
  useEffect(() => {
    if (!customer?.id || !hasModule(p.device, 'packages')) { setPackages([]); return; }
    void p.store.catalog('customer').then((all) => setPackages(((all.find((c) => c.id === customer.id)?.data.packages ?? []) as CustomerPackage[])));
  }, [customer, p.store, p.device, p.sync?.lastSyncAt]);
  // Contratos de aluguer do cliente com meses por cobrar (sem os já cobrados nesta caixa e por enviar).
  const [rentals, setRentals] = useState<{ contract: Contract; periods: string[] }[]>([]);
  const inCart = new Set(cart.filter((l) => l.rental).map((l) => `${l.rental!.contractId}|${l.rental!.period}`));
  const rentalsLeft = rentals.map((r) => ({ ...r, periods: r.periods.filter((x) => !inCart.has(`${r.contract.id}|${x}`)) })).filter((r) => r.periods.length);
  const addRentals = () => {
    const add: Line[] = [];
    for (const r of rentalsLeft) {
      const product = products.find((x) => x.id === r.contract.productId);
      if (!product) continue;
      for (const per of r.periods) {
        const line = rentalLine(r.contract, per, product);
        add.push({ key: crypto.randomUUID(), product, qty: '1', discountPct: '0', price: r.contract.monthlyFee, rental: { contractId: r.contract.id, period: per, label: line.description } });
      }
    }
    setCart((c) => [...c, ...add]);
  };
  const [creditSale, setCreditSale] = useState(false);
  const [receiving, setReceiving] = useState<'amount' | { amount: string } | null>(null);
  useEffect(() => {
    if (!customer?.id || !hasModule(p.device, 'credit')) { setAccount(null); return; }
    void p.store.catalog('customer').then((all) => {
      const d = all.find((c) => c.id === customer.id)?.data;
      setAccount(d && d.creditLimit != null ? { limit: String(d.creditLimit), balance: String(d.creditBalance ?? '0'), termsDays: Number(d.creditTermsDays ?? 30) } : d && Number(d.creditBalance ?? 0) > 0 ? { limit: null, balance: String(d.creditBalance), termsDays: 30 } : null);
    });
  }, [customer, p.store, p.device, p.sync?.lastSyncAt]);
  const [usePoints, setUsePoints] = useState(false);
  useEffect(() => {
    setUsePoints(false);
    if (!customer?.id || !p.device.company.loyaltyEnabled) { setCustPoints(0); return; }
    void p.store.catalog('customer').then((all) => setCustPoints(Number(all.find((c) => c.id === customer.id)?.data.points ?? 0)));
  }, [customer, p.store, p.device.company.loyaltyEnabled]);
  const [returning, setReturning] = useState(p.openOnStart === 'return');
  // Descontos da venda: por linha (Line.discountPct) e no total; a autorização do gerente vale para a venda.
  const [globalDiscount, setGlobalDiscount] = useState('0');
  const [discountApprover, setDiscountApprover] = useState<Operator | null>(null);
  const [discountFor, setDiscountFor] = useState<{ kind: 'line'; id: string; name: string; current: string } | { kind: 'total' } | null>(null);
  const [last, setLast] = useState<IssuedDocument | null>(null);
  useEffect(() => {
    if (!hasModule(p.device, 'deposits')) return;
    void p.store.catalog('reservation').then((i) => setOpenReservations(i.map((x) => x.data as unknown as Reservation).filter((r) => r.status === 'open' && (!r.storeId || r.storeId === p.device.store.id)).length));
  }, [p.store, p.device, p.sync?.lastSyncAt, last, reservationsOpen]);
  // Relê os meses por cobrar depois de cada venda (os cobrados ficam na fila de envio).
  useEffect(() => {
    if (!customer?.id || !hasModule(p.device, 'contracts')) { setRentals([]); return; }
    void (async () => {
      const charged = pendingCharges(await p.store.pendingOutbox(5000));
      const mine = (await p.store.catalog('contract')).map((i) => i.data as unknown as Contract).filter((k) => k.customerId === customer.id && k.active);
      setRentals(mine.map((k) => ({ contract: k, periods: duePeriods(k, fiscalDate(), charged) })).filter((r) => r.periods.length));
    })();
  }, [customer, p.store, p.device, p.sync?.lastSyncAt, last]);
  const [variantsOf, setVariantsOf] = useState<Product | null>(null);
  const [serialFor, setSerialFor] = useState<{ product: Product; lot?: LotInfo; price?: string } | null>(null);
  // Farmácia: receita pedida antes de pagar; substituto de uma linha.
  const [rx, setRx] = useState<Prescription | null>(null);
  const [askRx, setAskRx] = useState(false);
  const [substFor, setSubstFor] = useState<Line | null>(null);
  // Troca: a NC já saiu; o valor dela paga (parte de) a compra nova.
  const [exchange, setExchange] = useState<IssuedDocument | null>(null);
  const [weighing, setWeighing] = useState<Product | null>(null);
  const [pricing, setPricing] = useState<Product | null>(null);
  // Idade confirmada nesta venda (vale para todos os artigos 18+ dela); o artigo à espera da confirmação.
  const [ageOk, setAgeOk] = useState(false);
  const [askAge, setAskAge] = useState<Product | null>(null);
  const [hwOpen, setHwOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dark, toggleTheme] = useTheme();
  const wide = useMedia('(min-width: 1024px)');
  const search = useRef<HTMLInputElement>(null);

  // Senha da fila a cobrar: os serviços dela entram no carrinho, com o preço e o profissional de cada um.
  const loadedOrder = useRef<string | null>(null);
  // A senha/marcação fecha-se com a primeira venda; as seguintes, no mesmo ecrã, já não a levam.
  const [orderDone, setOrderDone] = useState(false);
  const order = p.fromOrder && !orderDone ? p.fromOrder : null;
  useEffect(() => {
    const o = p.fromOrder;
    if (!o || loadedOrder.current === o.id || !products.length) return;
    loadedOrder.current = o.id;
    setCart(billableItems(o).map((i) => {
      const product = products.find((x) => x.id === i.productId) ?? ({ id: i.productId, categoryId: null, code: '', name: i.description, barcode: null, unit: 'un', unitPrice: i.unitPrice, priceIncludesTax: i.priceIncludesTax, taxRate: i.taxRate, exemptionCode: i.exemptionCode ?? null, active: true } as Product);
      return { key: i.id, product, qty: i.quantity, discountPct: '0', price: i.unitPrice, ...(i.staffId ? { staffId: i.staffId } : {}), ...(i.serial ? { serial: i.serial, warrantyUntil: i.warrantyUntil ?? null } : {}) };
    }));
    if (o.queue?.servingBy) setSaleStaff(o.queue.servingBy);
    const cid = o.queue?.customer.id ?? o.work?.customer.id;
    // Ordem de serviço de um cliente sem ficha: a factura-recibo leva o nome dele.
    if (!cid && o.work) setCustomer({ name: o.work.customer.name, nuit: null });
    if (cid) void p.store.catalog('customer').then((all) => { const c = all.find((x) => x.id === cid)?.data; if (c) setCustomer({ id: cid, name: String(c.name), nuit: (c.nuit as string | null) ?? null }); });
  }, [p.fromOrder, products, p.store]);

  // Entrega: o que o cliente leva habitualmente entra no carrinho (uma vez, com o catálogo já lido).
  const loadedItems = useRef(false);
  useEffect(() => {
    if (loadedItems.current || !p.initialItems?.length || !products.length) return;
    loadedItems.current = true;
    setCart(p.initialItems.flatMap((i) => {
      const product = products.find((x) => x.id === i.productId);
      return product ? [{ key: crypto.randomUUID(), product, qty: i.quantity, discountPct: '0' }] : [];
    }));
  }, [p.initialItems, products]);
  const issuedOnce = useRef(false);
  const issued = (doc: IssuedDocument) => { if (p.onIssued && !issuedOnce.current) { issuedOnce.current = true; p.onIssued(doc); } };

  // O catálogo muda com a sincronização: relê-o depois de cada volta.
  useEffect(() => {
    void (async () => {
      setProducts((await p.store.catalog('product')).map(asProduct).filter((x) => x.active).sort((a, b) => a.name.localeCompare(b.name, 'pt')));
      setCategories((await p.store.catalog('category')).map(asCategory).filter((x) => x.active).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'pt')));
      setPromos((await p.store.catalog('promotion')).map((i) => i.data as unknown as Promotion));
      setLots((await p.store.catalog('lot')).map((i) => i.data as unknown as LotInfo & { productId: string }));
    })();
  }, [p.store, p.sync?.lastSyncAt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F2') { e.preventDefault(); search.current?.focus(); }
      if (e.key === 'F12' && cart.length && !paying) { e.preventDefault(); if (cart.some((l) => l.product.requiresPrescription || l.product.controlled) && !rx) setAskRx(true); else setPaying(true); }
      // Atalhos da barra lateral: só com o ecrã de venda à vista (nenhuma janela aberta por cima).
      if (document.querySelector('dialog[open]')) return;
      if (e.key === 'F4') { e.preventDefault(); setPickCustomer(true); }
      if (e.key === 'F6' && staff.length) { e.preventDefault(); setPickStaff({}); }
      if (e.key === 'F8' && p.licence?.sell) { e.preventDefault(); setReturning(true); }
      if (e.key === 'F9') { e.preventDefault(); setCashOpen(true); }
      if (e.key === 'F10' && p.onGestao) { e.preventDefault(); p.onGestao(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cart, paying, rx, p.licence?.sell, p.onGestao, staff.length]);

  // Promoções em vigor aplicadas ao carrinho (as ofertas saem em linhas à parte); `key` liga-as à linha do carrinho.
  const promoLines = useMemo(() => applyPromotions(cart.filter((l) => !l.pkgId).map((l): PromoLine & { key: string } => ({
    key: l.key, ...(l.staffId ? { staffId: l.staffId } : {}), productId: l.product.id, description: l.rental?.label ?? (l.lot ? `${l.product.name} (lote ${l.lot.number})` : l.product.name), quantity: l.qty, unitPrice: linePrice(l),
    ...(l.rental ? { contractId: l.rental.contractId, period: l.rental.period } : {}),
    ...(l.serial ? { serial: l.serial, warrantyUntil: l.warrantyUntil ?? null } : {}),
    ...(l.lot ? { lotId: l.lot.id } : {}),
    ...(l.product.requiresPrescription ? { requiresPrescription: true } : {}), ...(l.product.controlled ? { controlled: true } : {}), ...(l.product.regulatedPrice ? { regulatedPrice: true } : {}),
    ...(l.subst ? { substitutedFrom: l.subst } : {}),
    ...(l.product.admits ? { admits: l.product.admits, ...(l.product.ticket ? { ticket: true } : {}), ...(l.product.eventDate ? { eventDate: l.product.eventDate } : {}) } : {}),
    taxRate: l.product.taxRate, priceIncludesTax: l.product.priceIncludesTax, exemptionCode: l.product.exemptionCode,
    categoryId: l.product.categoryId, soldByWeight: !!l.product.soldByWeight, collection: l.product.collection ?? null,
    ...(l.discountPct !== '0' ? { discountPct: l.discountPct } : {}),
  })), promos, new Date(clock)) as (PromoLine & { key: string })[], [cart, promos, clock]);
  /** Só os campos do documento (sem os auxiliares do carrinho). */
  const lines: CartLine[] = promoLines.map(({ key: _k, categoryId: _c, soldByWeight: _w, collection: _col, ...l }) => l);
  // O que se vai emitir: o desconto no total já aplicado a cada linha (como no pos-core; não toca nas promoções).
  // Os pontos não se juntam a outros descontos: com eles, não há desconto no total nem manual.
  const loyalty = useMemo(() => (usePoints && custPoints > 0 && globalDiscount === '0' && !hasDiscount(lines) ? applyLoyalty(lines, custPoints) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promoLines, usePoints, custPoints, globalDiscount]);
  const effLines = useMemo(() => loyalty?.lines ?? applyGlobalDiscount(lines, globalDiscount),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promoLines, globalDiscount, loyalty]);
  const totals = useMemo(() => {
    try { return cart.length ? cartTotals(effLines) : null; } catch { return null; }
  }, [cart.length, effLines]);
  const savings = totals ? totals.lines.reduce((s, l) => s.plus(l.discountAmount), new D(0)) : new D(0);
  const resetSale = () => { setPackages([]); setAgeOk(false); setCart([]); setCustomer(null); setUsePoints(false); setGlobalDiscount('0'); setDiscountApprover(null); setRx(null); };

  // Modelos com variantes: um só botão, que abre a escolha (tamanho, cor…).
  const variants = useMemo(() => {
    const m = new Map<string, Product[]>();
    for (const x of products) if (x.variantOf) m.set(x.variantOf, [...(m.get(x.variantOf) ?? []), x]);
    return m;
  }, [products]);
  const visible = products.filter((x) => {
    if (x.variantOf && products.some((m) => m.id === x.variantOf)) return false;
    if (x.kind === 'container') return false; // o vasilhame junta-se sozinho (módulo próprio)
    if (cat && x.categoryId !== cat) return false;
    if (!q.trim()) return x.posVisible !== false;
    // A pesquisa encontra também os que não têm botão (ingredientes vendidos à parte, por exemplo).
    const s = q.trim().toLowerCase();
    return x.name.toLowerCase().includes(s) || x.code.toLowerCase() === s || x.barcode === q.trim() || !!x.extraBarcodes?.includes(q.trim())
      || !!x.shortName?.toLowerCase().includes(s) || !!x.brand?.toLowerCase().includes(s) || !!x.tags?.some((t) => t.includes(s));
  });

  /** Toque no artigo: modelo → escolher a variante; ao peso → pedir o peso; senão, mais um. */
  const pick = (product: Product, confirmed = false) => {
    if (product.ageRestricted && !ageOk && !confirmed) { setAskAge(product); return; }
    if (variants.get(product.id)?.some((v) => v.active)) setVariantsOf(product);
    else if (product.soldByWeight) setWeighing(product);
    else if (product.openPrice) setPricing(product);
    else add(product);
  };
  const add = (product: Product, qty = '1', separate = false, lot?: LotInfo, price?: string, serial?: string) => {
    // Número de série obrigatório: pede-se em cada unidade (uma linha por unidade).
    if (product.serialRequired && !serial) { setSerialFor({ product, lot, price }); return; }
    // Farmácia: se todos os lotes conhecidos do artigo estão vencidos ou recolhidos, não se vende.
    const known = lots.filter((x) => x.productId === product.id);
    if (product.trackLots && !lot && known.length && known.every((x) => lotProblem(x, today()))) {
      setError(`${product.name}: só há lotes vencidos ou recolhidos. Retire-os da prateleira (abate na gestão).`);
      return;
    }
    setLast(null);
    setCart((c) => {
      const sid = staff.length ? saleStaff ?? undefined : undefined;
      const i = separate || price || serial ? -1 : c.findIndex((l) => l.product.id === product.id && !l.product.soldByWeight && (!l.price || !!l.staffId) && l.lot?.id === lot?.id && l.staffId === sid);
      if (i < 0) {
        const line: Line = { key: crypto.randomUUID(), product, qty: serial ? '1' : qty, discountPct: '0', ...(lot ? { lot } : {}), ...(price ? { price } : {}), ...(serial ? { serial, warrantyUntil: warrantyEnd(today(), product.warrantyMonths) } : {}) };
        return [...c, sid && !price ? withStaff(line, sid) : sid ? { ...line, staffId: sid } : line];
      }
      const n = [...c];
      n[i] = { ...n[i]!, qty: new D(n[i]!.qty).plus(qty).toString() };
      return n;
    });
  };
  const setQty = (key: string, qty: Dec) => setCart((c) => (qty.lessThanOrEqualTo(0) ? c.filter((l) => l.key !== key) : c.map((l) => (l.key === key ? { ...l, qty: qty.toString() } : l))));

  // Leitor de código de barras = teclado que escreve e carrega em Enter.
  const onSearchEnter = () => {
    const s = q.trim();
    if (!s) return;
    // Embalagem de medicamento (GS1 DataMatrix): artigo pelo GTIN; o lote lido não pode estar vencido nem recolhido.
    const gs1 = /^(\]d2|\(01\)|01\d{14})/.test(s) ? parseGs1(s) : null;
    if (gs1?.gtin) {
      const prod = products.find((x) => x.barcode === gs1.gtin || x.barcode === gs1.gtin!.replace(/^0/, ''));
      if (!prod) { setError(`Nenhum artigo com o código ${gs1.gtin}`); return; }
      const lot = gs1.lot ? lots.find((x) => x.productId === prod.id && x.number === gs1.lot) : undefined;
      const info = lot ?? (gs1.lot && gs1.expiry ? { id: '', number: gs1.lot, expiry: gs1.expiry, recalled: false } : undefined);
      const problem = info ? lotProblem(info, today()) : null;
      if (problem) { setError(`${prod.name}: ${problem}`); setQ(''); return; }
      add(prod, '1', false, lot);
      setQ('');
      return;
    }
    const exact = products.find((x) => x.barcode === s || !!x.extraBarcodes?.includes(s) || x.code.toLowerCase() === s.toLowerCase());
    // Etiqueta da balança (prefixo 2): artigo pelo código da balança, quantidade pelo peso ou pelo preço.
    // Etiquetas da balança só com o módulo: sem ele, um código interno "2…" não se confunde com uma pesagem.
    const scan = exact || !hasModule(p.device, 'scale') ? null : parseScaleBarcode(s, p.device.company.scaleBarcodeValue ?? 'weight');
    if (scan) {
      const w = products.find((x) => x.scalePlu === scan.plu);
      if (!w) { setError(`Etiqueta da balança com o código ${scan.plu}, que nenhum artigo tem`); return; }
      add(w, scaleQuantity(scan, w.unitPrice), true);
      setQ('');
      return;
    }
    const found = exact ?? (visible.length === 1 ? visible[0] : undefined);
    if (found) { (exact && !found.openPrice && !found.ageRestricted ? add(found) : pick(found)); setQ(''); } else setError(`Nenhum artigo com o código "${s}"`);
  };

  /** Sessões de pacote das linhas marcadas; com a senha da fila/marcação toda em pacote, fecha-a. */
  const registerSessions = async (closeOrder: boolean) => {
    const used = cart.filter((l) => l.pkgId);
    for (const l of used) {
      const pkg = packages.find((k) => k.id === l.pkgId)!;
      for (let i = 0; i < Number(l.qty); i++) {
        await usePackageSession(p.store, identity(p.device), { customerId: customer!.id!, pkg: { ...pkg, remaining: pkg.remaining - i }, staffId: l.staffId ?? null, appointmentId: order?.appointmentId ?? null, operatorId: p.operator.id });
      }
    }
    if (closeOrder && order) {
      setOrderDone(true);
      const at = new Date().toISOString();
      const o = order;
      await saveOrder(p.store, identity(p.device), { ...o, status: 'closed', closedAt: at, updatedAt: at, ...(o.queue ? { queue: { ...o.queue, status: 'done' as const } } : {}), items: o.items.map((i) => (i.status === 'cancelled' || i.paidIn ? i : { ...i, paidIn: 'Pacote', updatedAt: at })) });
    }
    return used.reduce((n, l) => n + Number(l.qty), 0);
  };
  const onlyPackages = cart.length > 0 && cart.every((l) => l.pkgId);
  const sell = !!p.licence?.sell;
  // O desconto no total e os pontos espalham-se por todas as linhas: não com artigos "sem desconto".
  const noDiscountInCart = cart.some((l) => l.product.noDiscount);
  const training = p.device.company.trainingMode;

  const companyName = p.device.company.tradeName ?? p.device.company.legalName;
  const countIn = (id: string | null) => products.filter((x) => !(x.variantOf && products.some((m) => m.id === x.variantOf)) && (!id || x.categoryId === id)).length;
  const items = cart.reduce((s, l) => s.plus(l.product.soldByWeight ? 1 : l.qty), new D(0));

  return (
    <div className="flex h-full flex-col-reverse md:flex-row">
      <Rail label="Caixa" footer={<>
        {p.onGestao && <RailItem icon={LayoutDashboard} label="Gestão" kbd="F10" ariaLabel="Abrir a gestão" onClick={p.onGestao} />}
        <RailTool icon={Plug} label="Equipamento" onClick={() => setHwOpen(true)} />
        <RailTool icon={dark ? Sun : Moon} label={dark ? 'Modo claro' : 'Modo escuro'} onClick={toggleTheme} />
        <RailTool icon={RefreshCw} label="Sincronizar agora" onClick={p.onSyncNow} />
        <RailTool label="Trocar de operador" onClick={p.onLock}>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-brand-700 text-sm font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
        </RailTool>
      </>}>
        {p.onTables && <RailItem icon={p.tablesLabel ? Beer : LayoutGrid} label={p.tablesLabel ?? 'Mesas'} onClick={p.onTables} />}
        {p.onAccess && <RailItem icon={DoorOpen} label="Entradas" onClick={p.onAccess} />}
        {p.onDoor && <RailItem icon={Ticket} label="Porta" onClick={p.onDoor} />}
        {p.onDeliveries && <RailItem icon={Truck} label="Entregas" onClick={p.onDeliveries} />}
        {p.onAgenda && <RailItem icon={CalendarDays} label="Agenda" onClick={p.onAgenda} />}
        {p.onQueue && <RailItem icon={ListOrdered} label="Fila" onClick={p.onQueue} />}
        {p.onWorkOrders && <RailItem icon={ClipboardList} label="Ordens" onClick={p.onWorkOrders} />}
        <RailItem icon={ShoppingCart} label={p.onTables ? 'Balcão' : 'Vender'} active badge={cart.length || null} />
        <RailItem icon={Undo2} label="Devolução" kbd="F8" disabled={!sell} onClick={() => setReturning(true)} />
        <RailItem icon={Wallet} label="Caixa" kbd="F9" onClick={() => setCashOpen(true)} />
      </Rail>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex min-h-16 flex-wrap items-center gap-x-4 gap-y-2 border-b border-base bg-[var(--surface)] px-4 py-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[17px] font-semibold leading-tight">{companyName}</div>
            <div className="truncate text-xs text-muted">{p.device.store.name} · {p.device.terminal.name}</div>
          </div>
          {training && <span className="rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-black">MODO FORMAÇÃO — sem valor fiscal</span>}
          <div className="ml-auto flex items-center gap-2">
            {applyUpdate && !cart.length && !paying && (
              <button onClick={applyUpdate} className="h-8 rounded-full bg-ink px-3 text-sm font-semibold text-white dark:bg-white dark:text-ink">Nova versão — actualizar</button>
            )}
            <StatusPill sync={p.sync} />
            <span className="hidden items-center gap-2 rounded-full bg-surface-2 py-1 pl-1 pr-3 text-sm font-medium sm:flex">
              <span aria-hidden className="grid h-6 w-6 place-items-center rounded-full bg-brand-700 text-xs font-bold text-white">{p.operator.name.slice(0, 1).toUpperCase()}</span>
              {p.operator.name}
            </span>
          </div>
        </header>

        {!sell && p.licence && <div role="alert" className="bg-brand-700 px-4 py-2 text-center font-medium text-white">{p.licence.reason}</div>}
        {info && (
          <div role="status" className="flex items-center gap-2 bg-green-50 px-4 py-2 text-green-900 dark:bg-green-950 dark:text-green-100">
            <span className="flex-1">{info}</span>
            <button aria-label="Fechar aviso" onClick={() => setInfo(null)} className="grid h-10 w-10 place-items-center"><X size={18} /></button>
          </div>
        )}
        {error && (
          <div role="alert" className="flex items-center gap-2 bg-amber-100 px-4 py-2 text-amber-900">
            <span className="flex-1">{error}</span>
            <button aria-label="Fechar aviso" onClick={() => setError(null)} className="grid h-12 w-12 place-items-center"><X size={18} /></button>
          </div>
        )}

        <div className="flex min-h-0 flex-1">
          {wide && (
            <SidePanel title="Categorias" subtitle={`${countIn(null)} artigos`}>
              <div className="space-y-0.5">
                <PanelItem active={!cat} onClick={() => setCat(null)} color={null} trailing={countIn(null)}>Todos</PanelItem>
                {categories.map((c) => <PanelItem key={c.id} active={cat === c.id} onClick={() => setCat(c.id)} color={c.color ?? null} trailing={countIn(c.id)}>{c.name}</PanelItem>)}
              </div>
            </SidePanel>
          )}

      <main className="grid min-h-0 min-w-0 flex-1 grid-cols-1 md:grid-cols-[1fr_360px] xl:grid-cols-[1fr_420px]">
        {/* Artigos */}
        <section className="flex min-h-0 flex-col p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={20} />
            <input
              ref={search}
              autoFocus
              type="search"
              value={q}
              onChange={(e) => { setQ(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSearchEnter(); } }}
              placeholder="Pesquisar ou ler código de barras"
              className="h-14 w-full rounded-2xl border border-base bg-[var(--surface)] pl-12 pr-16 text-lg shadow-sm outline-none transition focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10"
            />
            <kbd className="pointer-events-none absolute right-4 top-1/2 hidden -translate-y-1/2 rounded-md border border-base bg-surface-2 px-1.5 py-0.5 text-xs text-muted sm:block">F2</kbd>
          </div>
          {!wide && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              <Chip active={!cat} onClick={() => setCat(null)}>Todos</Chip>
              {categories.map((c) => <Chip key={c.id} active={cat === c.id} color={c.color} onClick={() => setCat(c.id)}>{c.name}</Chip>)}
            </div>
          )}
          <div className="mt-4 grid min-h-0 flex-1 auto-rows-min grid-cols-2 gap-3 overflow-y-auto pb-2 sm:grid-cols-3 2xl:grid-cols-5 xl:grid-cols-4">
            {visible.map((x) => {
              const color = x.buttonColor ?? categories.find((c) => c.id === x.categoryId)?.color;
              return (
                <button key={x.id} onClick={() => pick(x)} title={x.shortName ? x.name : undefined} className="surface group relative flex min-h-28 flex-col justify-between overflow-hidden rounded-2xl p-3.5 text-left shadow-xs transition hover:border-brand-500 hover:shadow-md active:scale-[.98]">
                  <span aria-hidden className="absolute inset-x-0 top-0 z-10 h-1" style={{ background: color ?? 'transparent' }} />
                  {x.image && <img src={x.image} alt="" loading="lazy" className="-mx-3.5 -mt-3.5 mb-2.5 aspect-[4/3] w-[calc(100%+1.75rem)] max-w-none object-cover" />}
                  <span className={`line-clamp-2 font-medium leading-snug ${!x.image && (x.ageRestricted || x.durationMin) ? 'pr-14' : ''}`}>{x.shortName || x.name}</span>
                  {variants.get(x.id)?.length
                    ? <span className="mt-2 text-sm text-muted">{variants.get(x.id)!.filter((v) => v.active).length} variantes</span>
                    : (
                      <span className="mt-2 flex items-baseline justify-between gap-2">
                        {x.openPrice
                          ? <span className="text-sm font-semibold text-muted">Preço livre</span>
                          : <span className="num text-lg font-bold">{formatMoney(x.unitPrice)} <small className="text-xs font-normal text-muted">MT{x.soldByWeight ? '/kg' : ''}</small></span>}
                      </span>
                    )}
                  {/* Depois do nome (leitores de ecrã lêem o nome primeiro); fica no canto. */}
                  {(x.ageRestricted || x.durationMin) && (
                    <span className="absolute right-2 top-2.5 z-10 rounded-full bg-[var(--surface)]/90 px-2 py-0.5 text-[11px] font-semibold text-muted shadow-xs">{x.ageRestricted ? '18+' : `${x.durationMin} min`}</span>
                  )}
                </button>
              );
            })}
            {!visible.length && (
              <p className="col-span-full p-6 text-center text-muted">
                {products.length ? 'Nenhum artigo encontrado.' : 'Ainda não há artigos. Crie-os no backoffice; aparecem aqui na próxima sincronização.'}
              </p>
            )}
          </div>
        </section>

        {/* Carrinho */}
        <aside className="flex min-h-0 flex-col border-t border-base bg-[var(--surface)] md:border-l md:border-t-0">
          <div className="flex items-center justify-between px-4 pt-3">
            <h2 className="font-display text-base font-semibold">Venda actual</h2>
            {cart.length > 0 && <span className="num text-sm text-muted">{items.toString().replace('.', ',')} {items.equals(1) ? 'artigo' : 'artigos'}</span>}
          </div>
          {/* Com cliente, a venda sai como factura-recibo; sem cliente, venda a dinheiro. */}
          <div className="flex items-center gap-2 border-b border-base px-3 py-2">
            <button onClick={() => setPickCustomer(true)} title="Cliente (F4)" className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-xl bg-surface-2 px-3 text-left">
              <User size={18} className="shrink-0" />
              <span className="truncate">{customer ? `${customer.name}${customer.nuit ? ` · ${customer.nuit}` : ''}` : 'Consumidor Final'}</span>
              <span className="ml-auto shrink-0 text-xs text-muted">{customer ? 'Factura-recibo' : 'Venda a dinheiro'}</span>
            </button>
            {customer && <button aria-label="Tirar o cliente" onClick={() => setCustomer(null)} className="grid h-12 w-12 place-items-center rounded-lg"><X size={18} /></button>}
          </div>
          {account && (
            <div className="flex items-center gap-2 border-b border-base px-4 py-2 text-sm">
              <span className="num min-w-0 flex-1 text-muted">
                Conta corrente: deve <b className={new D(account.balance).greaterThan(0) ? 'text-[var(--text)]' : ''}>{formatMoney(account.balance)} MT</b>
                {account.limit !== null && <> · limite {formatMoney(account.limit)}</>}
              </span>
              {new D(account.balance).greaterThan(0) && sell && (
                <button onClick={() => setReceiving('amount')} className="h-10 shrink-0 rounded-lg border border-base px-3 font-semibold hover:bg-surface-2">Receber</button>
              )}
            </div>
          )}
          {rentalsLeft.length > 0 && (
            <div role="status" className="flex items-center gap-2 border-b border-base bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
              <span className="min-w-0 flex-1">Aluguer por cobrar: <b>{rentalsLeft.map((r) => r.periods.map(periodLabel).join(', ')).join(' · ')}</b></span>
              {sell && <button onClick={addRentals} className="h-10 shrink-0 rounded-lg border border-amber-700/40 px-3 font-semibold">Juntar à venda</button>}
            </div>
          )}
          {staff.length > 0 && (() => {
            const cur = staff.find((x) => x.id === saleStaff);
            return (
              <button onClick={() => setPickStaff({})} title="Quem atende (F6)" className="flex h-12 items-center gap-2 border-b border-base px-4 text-left text-sm hover:bg-surface-2">
                <span className="text-muted">Atende</span>
                {cur ? <><StaffAvatar s={cur} size={28} /><span className="font-semibold">{cur.name}</span></> : <span className="font-semibold text-brand-700">escolher profissional</span>}
                <kbd className="ml-auto rounded-md border border-base bg-surface-2 px-1.5 py-0.5 text-xs text-muted">F6</kbd>
              </button>
            );
          })()}
          {exchange && (
            <div role="status" className="flex flex-wrap items-center gap-2 border-b border-base bg-sky-50 px-3 py-2 text-sky-900 dark:bg-sky-950 dark:text-sky-100">
              <span className="flex-1 text-sm"><b>Troca:</b> crédito de <b className="num">{formatMoney(exchange.grossTotal)} MT</b> ({exchange.fullNumber}). Junte os artigos novos e carregue em PAGAR.</span>
              <button onClick={() => { setError(`Troca desistida: entregue ${formatMoney(exchange.grossTotal)} MT em dinheiro ao cliente.`); setExchange(null); }} className="h-9 rounded-lg border border-current px-2 text-sm">Devolver em dinheiro</button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-3">
            {!cart.length && hasModule(p.device, 'deposits') && sell && (
              <button onClick={() => setReservationsOpen(true)} className="mb-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-base font-semibold text-muted hover:border-brand-500 hover:text-[var(--text)]"><Bookmark size={18} /> Reservas{openReservations ? ` (${openReservations})` : ''}</button>
            )}
            {!cart.length && voucherLookup && sell && (
              <button onClick={() => setSelling('form')} className="mb-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-base font-semibold text-muted hover:border-brand-500 hover:text-[var(--text)]"><Gift size={18} /> Vender vale-oferta</button>
            )}
            {!cart.length && !last && (
              <div className="grid h-full place-items-center p-6 text-center text-muted">
                <div>
                  <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-surface-2"><ShoppingCart size={26} strokeWidth={1.6} /></span>
                  <p className="mt-3">Toque nos artigos ou leia o código de barras.</p>
                  <p className="mt-1 text-xs">F2 pesquisar · F4 cliente · F12 pagar</p>
                </div>
              </div>
            )}
            {!cart.length && last && (
              <div className="rounded-xl bg-green-50 p-4 text-green-900 dark:bg-green-950 dark:text-green-100">
                <div className="font-semibold">{last.fullNumber} {last.docType === 'NC' ? (exchange === last ? `emitida — ${formatMoney(last.grossTotal)} MT para a troca` : `emitida — devolver ${formatMoney(last.grossTotal)} MT`) : 'emitida'}</div>
                {last.voucher && <div className="mt-1 font-mono text-2xl font-bold tracking-wider">Vale {last.voucher.code}</div>}
                {new D(last.change).greaterThan(0) && <div className="num mt-1 text-3xl font-bold">Troco {formatMoney(last.change)} MT</div>}
                <button onClick={() => void printThermal(receiptHtml(last, p.device, { copy: '2.ª Via' }))} className="mt-3 flex h-12 items-center gap-2 rounded-lg border border-green-700 px-3">
                  <Printer size={18} /> Imprimir 2.ª via
                </button>
              </div>
            )}
            <ul className="divide-y divide-[var(--border)]">
              {cart.map((l) => {
                const price = linePrice(l);
                const tier = !l.price && !new D(price).equals(l.product.unitPrice);
                const mine = promoLines.flatMap((x, j) => (x.key === l.key ? [j] : []));
                const rowTotal = totals ? mine.reduce((s, j) => s.plus(totals.lines[j]!.totalAmount), new D(0)) : null;
                const promoNames = [...new Set(mine.map((j) => promoLines[j]!.promotionId).filter(Boolean).map((id) => promos.find((x) => x.id === id)?.name ?? 'Promoção'))];
                const free = mine.filter((j) => promoLines[j]!.promotionId && promoLines[j]!.discountPct === '100').reduce((s, j) => s.plus(promoLines[j]!.quantity), new D(0));
                return (
                <li key={l.key} className="py-3">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium leading-tight">{l.rental?.label ?? l.product.name}{l.lot && <> <span className="text-sm font-normal text-muted">(lote {l.lot.number})</span></>}</div>
                      {l.serial && <div className="font-mono text-xs text-muted">N.º série {l.serial}{l.warrantyUntil ? <span className="font-sans"> · garantia até {l.warrantyUntil.split('-').reverse().join('/')}</span> : null}</div>}
                      {(() => {
                        const pkg = packages.find((k) => k.serviceId === l.product.id);
                        if (!pkg) return null;
                        const inUse = cart.filter((x) => x.pkgId === pkg.id && x.key !== l.key).reduce((n, x) => n + Number(x.qty), 0);
                        const left = pkg.remaining - inUse;
                        return (
                          <button onClick={() => setCart((c) => c.map((x) => (x.key === l.key ? (x.pkgId ? { ...x, pkgId: undefined } : { ...x, pkgId: pkg.id }) : x)))}
                            disabled={!l.pkgId && left < Number(l.qty)} aria-pressed={!!l.pkgId}
                            className={`mr-1.5 mt-1 inline-flex h-8 items-center rounded-full px-3 text-xs font-semibold disabled:opacity-40 ${l.pkgId ? 'bg-green-600 text-white' : 'border border-green-600 text-green-700 dark:text-green-400'}`}>
                            {l.pkgId ? `Pacote · ficam ${left - Number(l.qty)}` : `Usar pacote · restam ${left}`}
                          </button>
                        );
                      })()}
                      {staff.length > 0 && (() => {
                        const who = staff.find((x) => x.id === l.staffId);
                        return (
                          <button onClick={() => setPickStaff({ line: l.key })} aria-label={`Profissional de ${l.product.name}`} className="mt-1 inline-flex h-8 items-center gap-1.5 rounded-full bg-surface-2 py-0.5 pl-0.5 pr-2.5 text-xs font-medium">
                            {who ? <><StaffAvatar s={who} size={24} />{who.name}</> : <span className="pl-2 text-brand-700">quem fez?</span>}
                          </button>
                        );
                      })()}
                      <div className="num text-sm text-muted">
                        {formatMoney(price)}{l.product.soldByWeight ? '/kg' : ''} × {l.product.soldByWeight ? `${l.qty.replace('.', ',')} kg` : l.qty.replace('.', ',')}
                        {tier && <span className="ml-1 font-semibold text-green-700 dark:text-green-400">preço por quantidade</span>}
                        {l.discountPct !== '0' && <span className="ml-1 font-semibold text-brand-700">−{l.discountPct.replace('.', ',')}%</span>}
                      </div>
                      {l.product.admits ? <div className="text-xs text-muted">{l.product.ticket ? `Bilhete com QR${l.product.eventDate ? ` · noite de ${l.product.eventDate.split('-').reverse().join('/')}` : ''}` : 'Entrada'} · {l.product.admits === 1 ? '1 pessoa' : `${l.product.admits} pessoas`}</div> : null}
                      {l.product.requiresPrescription && <div className="text-xs font-semibold text-brand-700">{l.product.controlled ? 'Controlo especial · receita' : 'Receita obrigatória'}</div>}
                      {l.subst && <div className="text-xs text-muted">em vez de {l.subst.description}{l.subst.prescriberConsent ? ' (prescritor consentiu)' : ''}</div>}
                      {promoNames.length > 0 && (
                        <div className="text-xs font-semibold text-green-700 dark:text-green-400">
                          {promoNames.join(' · ')}{free.greaterThan(0) ? ` — ${free.toString()} de oferta` : ''}
                        </div>
                      )}
                    </div>
                    <span className="num pt-0.5 text-right text-lg font-semibold">{l.pkgId ? <span className="text-sm text-green-700 dark:text-green-400">pacote</span> : rowTotal ? formatMoney(rowTotal) : '—'}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-end gap-2">
                    {l.product.activeIngredient && products.some((x) => x.id !== l.product.id && x.activeIngredient?.toLowerCase() === l.product.activeIngredient!.toLowerCase()) && (
                      <button onClick={() => setSubstFor(l)} className="h-12 rounded-lg bg-surface-2 px-3 text-sm font-medium">Substitutos</button>
                    )}
                    {l.product.regulatedPrice || l.product.noDiscount
                      ? <span title={l.product.regulatedPrice ? 'Preço regulado: sem descontos (art. 64 do Decreto 16/2023)' : 'Este artigo não aceita descontos'} className="rounded bg-surface-2 px-2 py-1 text-xs">{l.product.regulatedPrice ? 'preço regulado' : 'sem desconto'}</span>
                      : <button aria-label={`Desconto em ${l.product.name}`} onClick={() => setDiscountFor({ kind: 'line', id: l.key, name: l.product.name, current: l.discountPct })} className={`grid h-12 w-12 place-items-center rounded-lg ${l.discountPct !== '0' ? 'bg-brand-50 text-brand-700' : 'bg-surface-2'}`}><Percent size={18} /></button>}
                    {!l.product.soldByWeight && (
                      <span className="flex items-center rounded-xl bg-surface-2">
                        <button aria-label={`Menos ${l.product.name}`} onClick={() => setQty(l.key, new D(l.qty).minus(1))} className="grid h-12 w-12 place-items-center rounded-xl hover:bg-black/5 dark:hover:bg-white/10"><Minus size={18} /></button>
                        <span className="num w-9 text-center font-semibold">{l.qty.replace('.', ',')}</span>
                        <button aria-label={`Mais ${l.product.name}`} disabled={!!l.rental || !!l.serial} onClick={() => setQty(l.key, new D(l.qty).plus(1))} className="grid h-12 w-12 place-items-center rounded-xl hover:bg-black/5 disabled:opacity-30 dark:hover:bg-white/10"><Plus size={18} /></button>
                      </span>
                    )}
                    <button aria-label={`Retirar ${l.product.name}`} onClick={() => setQty(l.key, new D(0))} className="grid h-12 w-12 place-items-center rounded-xl text-muted hover:bg-brand-50 hover:text-brand-700"><Trash2 size={18} /></button>
                  </div>
                </li>
                );
              })}
            </ul>
          </div>
          <div className="border-t border-base bg-surface-2/60 p-4">
            {totals && (
              <div className="num mb-2 space-y-0.5 text-sm text-muted">
                <div className="flex items-center justify-between">
                  <button onClick={() => setDiscountFor({ kind: 'total' })} disabled={noDiscountInCart && globalDiscount === '0'} title={noDiscountInCart ? 'Há artigos que não aceitam descontos' : undefined} className="flex h-10 items-center gap-1 rounded-lg px-2 text-[var(--text)] hover:bg-surface-2 disabled:opacity-40"><Percent size={14} /> Desconto no total{globalDiscount !== '0' ? ` (${globalDiscount.replace('.', ',')}%)` : ''}</button>
                  {savings.greaterThan(0) && <span className="font-semibold text-brand-700">Descontos −{formatMoney(savings)}</span>}
                </div>
                {custPoints > 0 && (
                  <label className="flex h-9 items-center gap-2 px-2 text-[var(--text)]">
                    <input type="checkbox" aria-label="Usar pontos" checked={usePoints} disabled={globalDiscount !== '0' || hasDiscount(lines) || noDiscountInCart} onChange={(e) => setUsePoints(e.target.checked)} />
                    Usar pontos ({custPoints} disponíveis){loyalty ? ` — ${loyalty.pointsUsed} pontos, −${formatMoney(loyalty.discount)}` : ''}
                  </label>
                )}
                {totals.taxSummary.map((t) => <div key={t.rate} className="flex justify-between"><span>IVA {new D(t.rate).toString().replace('.', ',')}%</span><span>{formatMoney(t.taxAmount)}</span></div>)}
              </div>
            )}
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium uppercase tracking-wide text-muted">Total</span>
              <span className="num text-4xl font-bold">{formatMoney(totals?.grossTotal ?? '0')} <small className="text-base font-normal text-muted">MT</small></span>
            </div>
            {onlyPackages && (
              <button onClick={async () => {
                try {
                  const n = await registerSessions(true);
                  resetSale(); setInfo(`${n} sessão(ões) de pacote registada(s). Sem documento: o pacote já foi facturado.`); p.onSold();
                } catch (e) { setError((e as Error).message); }
              }} className="mt-3 h-16 w-full rounded-2xl bg-green-700 text-xl font-bold text-white">Registar sessão do pacote</button>
            )}
            {account?.limit != null && customer?.nuit && (
              <button disabled={!cart.length || !sell || !totals || usePoints} title={usePoints ? 'Os pontos usam-se em vendas a pronto' : undefined} onClick={() => setCreditSale(true)}
                className="mt-3 h-12 w-full rounded-2xl border-2 border-brand-700 font-semibold text-brand-700 disabled:opacity-40">A crédito (factura, paga depois)</button>
            )}
            {hasModule(p.device, 'deposits') && cart.length > 0 && (
              <button disabled={!sell || !totals || usePoints || globalDiscount !== '0'} title={globalDiscount !== '0' || usePoints ? 'Os descontos aplicam-se no levantamento' : undefined} onClick={() => setReserving('form')}
                className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border-2 border-brand-700 font-semibold text-brand-700 disabled:opacity-40"><Bookmark size={18} /> Reservar com sinal</button>
            )}
            <div className="mt-3 grid grid-cols-[auto_auto_1fr] gap-2">
              <button disabled={!cart.length} onClick={resetSale} className="h-16 rounded-2xl border border-base bg-[var(--surface)] px-4 font-medium disabled:opacity-40">Limpar</button>
              <button disabled={!cart.length || !totals} title="Pró-forma (orçamento): não é venda" onClick={async () => {
                try {
                  const pf = await emitProforma(p.store, identity(p.device), p.signer, { lines: effLines, customer, operatorId: p.operator.id });
                  resetSale(); setLast(pf); p.onSold();
                  void printThermal(receiptHtml(pf, p.device));
                } catch (e) { setError((e as Error).message); }
              }} className="flex h-16 items-center gap-1 rounded-2xl border border-base bg-[var(--surface)] px-3 font-medium disabled:opacity-40"><FileText size={18} /> Pró-forma</button>
              <button
                disabled={!cart.length || !sell || !totals}
                onClick={() => (lines.some((l) => l.requiresPrescription || l.controlled) && !rx ? setAskRx(true) : setPaying(true))}
                className="h-16 rounded-2xl bg-brand-700 text-2xl font-bold tracking-wide text-white shadow-lg shadow-brand-700/25 transition hover:bg-brand-800 active:scale-[.99] disabled:opacity-40 disabled:shadow-none"
              >PAGAR <span className="text-sm font-normal opacity-80">(F12)</span></button>
            </div>
          </div>
        </aside>
      </main>
        </div>
      </div>

      {cashOpen && (
        <CashPanel
          store={p.store} device={p.device} who={identity(p.device)} operator={p.operator}
          operatorName={(id) => operators.get(id) ?? '—'}
          onClose={() => setCashOpen(false)}
          onChanged={p.onCashChanged}
          onClosedSession={(z) => { setCashOpen(false); p.onSessionClosed(z); }}
        />
      )}
      {discountFor && (
        <DiscountDialog
          title={discountFor.kind === 'line' ? `Desconto em ${discountFor.name}` : 'Desconto no total'}
          current={discountFor.kind === 'line' ? discountFor.current : globalDiscount}
          store={p.store} operator={p.operator} approvedBy={discountApprover}
          onClose={() => setDiscountFor(null)}
          onApply={(pct, approver) => {
            setDiscountApprover(approver);
            if (discountFor.kind === 'line') setCart((c) => c.map((l) => (l.key === discountFor.id ? { ...l, discountPct: pct } : l)));
            else setGlobalDiscount(pct);
            setDiscountFor(null);
          }}
        />
      )}
      {variantsOf && (
        <VariantDialog model={variantsOf} variants={(variants.get(variantsOf.id) ?? []).filter((v) => v.active)} onClose={() => setVariantsOf(null)}
          onPick={(v) => { setVariantsOf(null); if (v.soldByWeight) setWeighing(v); else add(v); }}
          {...(hasModule(p.device, 'stock') ? {
            stock: () => terminalApi<StoreStock>(p.device, `/sync/stock?productId=${variantsOf.id}`),
            onRequest: async (fromStoreId: string, v: Product, note: string) => { await requestStock(p.store, identity(p.device), { fromStoreId, productId: v.id, quantity: '1', note, operatorId: p.operator.id }); p.onSyncNow(); },
          } : {})} />
      )}
      {hwOpen && <HardwareDialog onClose={() => setHwOpen(false)} />}
      {creditSale && totals && customer && account && (
        <CreditSaleDialog customerName={customer.name} total={totals.grossTotal} account={account} store={p.store} operator={p.operator}
          onClose={() => setCreditSale(false)}
          onConfirm={async (approvedBy) => {
            const doc = await emitSale(p.store, identity(p.device), p.signer, {
              docType: 'FT', customer, lines: effLines, tenders: [], globalDiscountPct: '0', discountApprovedBy: discountApprover?.id ?? null,
              credit: account, creditApprovedBy: approvedBy, ...(order ? { order: { id: order.id } } : {}), operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence!, prescription: rx,
            });
            setCreditSale(false); setRx(null); resetSale(); setExchange(null); setLast(doc); p.onSold(); setOrderDone(true); issued(doc);
            void printThermal(receiptHtml(doc, p.device, { staffNames }));
          }} />
      )}
      {receiving === 'amount' && customer && account && (
        <ReceiptAmountDialog customerName={customer.name} balance={account.balance} onClose={() => setReceiving(null)} onAmount={(amount) => setReceiving({ amount })} />
      )}
      {receiving && receiving !== 'amount' && customer && account && p.licence && (
        <PayDialog total={receiving.amount} hint={`Recibo de ${customer.name}`} onClose={() => setReceiving(null)}
          onConfirm={async (tenders) => {
            const doc = await emitReceipt(p.store, identity(p.device), p.signer, {
              customer, amount: receiving.amount, tenders, balance: account.balance, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence!,
            });
            setReceiving(null); setLast(doc); p.onSold();
            setAccount({ ...account, balance: new D(account.balance).minus(doc.grossTotal).toFixed(2) });
            void printThermal(receiptHtml(doc, p.device, { staffNames }), { drawer: doc.payments.some((x) => x.method === 'NU') });
          }} />
      )}
      {pickStaff && (() => {
        const line = pickStaff.line ? cart.find((x) => x.key === pickStaff.line) : undefined;
        return (
          <StaffPicker staff={staff} title={line ? `Quem fez — ${line.product.name}` : 'Quem atende'} current={line ? line.staffId ?? null : saleStaff}
            productId={line?.product.id} onClose={() => setPickStaff(null)}
            onPick={(id) => {
              // Na venda inteira: muda as linhas que ainda não tinham profissional e as que eram do anterior.
              if (line) setCart((c) => c.map((x) => (x.key === line.key ? withStaff(x, id) : x)));
              else { setCart((c) => c.map((x) => (!x.staffId || x.staffId === saleStaff ? withStaff(x, id) : x))); setSaleStaff(id); }
              setPickStaff(null);
            }} />
        );
      })()}
      {reserving === 'form' && totals && <ReserveDialog total={totals.grossTotal} customer={customer} onClose={() => setReserving(null)} onNext={(r) => setReserving(r)} />}
      {reserving && reserving !== 'form' && p.licence && (
        <PayDialog total={reserving.deposit} hint={`Sinal da reserva · ${reserving.customer.name}`} onClose={() => setReserving('form')}
          onConfirm={async (tenders) => {
            const items: ReservationItem[] = lines.map((l) => ({
              productId: l.productId, description: l.description, quantity: String(l.quantity), unitPrice: String(l.unitPrice), taxRate: String(l.taxRate), priceIncludesTax: l.priceIncludesTax,
              ...(l.exemptionCode ? { exemptionCode: l.exemptionCode } : {}), ...(l.discountPct ? { discountPct: String(l.discountPct) } : {}), ...(l.promotionId ? { promotionId: l.promotionId } : {}),
            }));
            const r = await createReservation(p.store, identity(p.device), p.signer, { items, customer: reserving.customer, dueDate: reserving.dueDate, deposit: reserving.deposit, tenders, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence! });
            // A caixa conhece já a reserva (a nuvem confirma-a na sincronização).
            await p.store.applyCatalog([{ type: 'reservation', id: r.reservation.id, version: 0, data: { ...r.reservation, storeId: p.device.store.id } as unknown as Record<string, unknown> }], await p.store.catalogCursor());
            setReserving(null); resetSale(); setLast(r.document); setInfo(`Reserva ${r.reservation.code} guardada para ${reserving.customer.name}`); p.onSold();
            void printThermal(receiptHtml(r.document, p.device), { drawer: r.document.payments.some((x) => x.method === 'NU') });
          }} />
      )}
      {reservationsOpen && p.licence && (
        <ReservationsDialog store={p.store} device={p.device} signer={p.signer} operator={p.operator} cashSessionId={p.cashSessionId} licence={p.licence}
          onClose={() => setReservationsOpen(false)}
          onDone={(docs, message) => {
            setReservationsOpen(false); resetSale(); if (docs[0]) setLast(docs[0]); setInfo(message); p.onSold();
            void (async () => { for (const [i, d] of docs.entries()) await printThermal(receiptHtml(d, p.device), { drawer: i === 0 && d.payments.some((x) => x.method === 'NU') }); })();
          }} />
      )}
      {selling === 'form' && <VoucherDialog onClose={() => setSelling(null)} onNext={(v) => setSelling(v)} />}
      {selling && selling !== 'form' && p.licence && (
        <PayDialog total={selling.amount} hint={`Vale-oferta de ${formatMoney(selling.amount)} MT${selling.buyer ? ` · ${selling.buyer}` : ''}`} onClose={() => setSelling(null)}
          onConfirm={async (tenders) => {
            const doc = await emitVoucher(p.store, identity(p.device), p.signer, {
              code: newVoucherCode(), amount: selling.amount, validityDays: selling.validityDays, buyer: selling.buyer ? { name: selling.buyer, nuit: null } : null,
              tenders, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence!,
            });
            setSelling(null); setLast(doc); p.onSold();
            void printThermal(receiptHtml(doc, p.device), { drawer: doc.payments.some((x) => x.method === 'NU') });
          }} />
      )}
      {serialFor && (
        <SerialDialog product={serialFor.product} taken={cart.flatMap((l) => (l.serial ? [l.serial] : []))} onClose={() => setSerialFor(null)}
          onSerial={(sn) => { const f = serialFor; setSerialFor(null); add(f.product, '1', true, f.lot, f.price, sn); }} />
      )}
      {pricing && <PriceDialog product={pricing} onClose={() => setPricing(null)} onPrice={(price) => { add(pricing, '1', true, undefined, price); setPricing(null); }} />}
      {askAge && <AgeDialog product={askAge} onClose={() => setAskAge(null)} onConfirm={() => { const x = askAge; setAgeOk(true); setAskAge(null); pick(x, true); }} />}
      {weighing && <WeightDialog product={weighing} onClose={() => setWeighing(null)} onWeight={(kg) => { add(weighing, kg, true); setWeighing(null); }} />}
      {pickCustomer && (
        <CustomerDialog store={p.store} who={identity(p.device)} onClose={() => setPickCustomer(false)} onPick={(c) => { setCustomer(c); setPickCustomer(false); p.onSold(); }} />
      )}
      {returning && p.licence && (
        <ReturnDialog
          store={p.store} device={p.device} who={identity(p.device)} signer={p.signer} operator={p.operator} cashSessionId={p.cashSessionId} licence={p.licence}
          onClose={() => setReturning(false)}
          onDone={(nc, ex) => { setReturning(false); resetSale(); setLast(nc); if (ex) setExchange(nc); p.onSold(); void printThermal(receiptHtml(nc, p.device), { drawer: nc.payments.some((x) => x.method === 'NU') }); }}
        />
      )}
      {askRx && (
        <PrescriptionDialog store={p.store} operator={p.operator} controlled={cart.some((l) => l.product.controlled)} onClose={() => setAskRx(false)}
          onDone={(r) => { setRx(r); setAskRx(false); setPaying(true); }} />
      )}
      {substFor && (
        <SubstituteDialog line={{ product: substFor.product, prescribed: !!substFor.product.requiresPrescription }}
          options={products.filter((x) => x.id !== substFor.product.id && x.activeIngredient?.toLowerCase() === substFor.product.activeIngredient?.toLowerCase())}
          onClose={() => setSubstFor(null)}
          onPick={(prod, consent) => {
            const orig = substFor.subst ?? { productId: substFor.product.id, description: substFor.product.name, prescriberConsent: consent };
            setCart((c) => c.map((x) => (x.key === substFor.key ? { ...x, product: prod, lot: undefined, discountPct: '0', subst: { ...orig, prescriberConsent: consent } } : x)));
            setSubstFor(null);
          }} />
      )}
      {paying && totals && p.licence && (
        <PayDialog
          voucherLookup={voucherLookup}
          total={totals.grossTotal}
          credit={exchange ? { amount: exchange.grossTotal, reference: `Troca ${exchange.fullNumber}` } : undefined}
          onClose={() => setPaying(false)}
          onConfirm={async (tenders) => {
            const doc = await emitSale(p.store, identity(p.device), p.signer, {
              docType: customer ? 'FR' : 'VD', customer, lines: loyalty?.lines ?? lines, globalDiscountPct: globalDiscount, discountApprovedBy: discountApprover?.id ?? null,
              ...(loyalty && loyalty.pointsUsed > 0 ? { loyaltyPoints: loyalty.pointsUsed } : {}),
              tenders, operatorId: p.operator.id, cashSessionId: p.cashSessionId, licence: p.licence!, prescription: rx,
              ...(order ? { order: { id: order.id } } : {}),
            });
            const n = cart.some((l) => l.pkgId) ? await registerSessions(false) : 0;
            setOrderDone(true);
            setPaying(false);
            setRx(null);
            resetSale();
            if (n) setInfo(`${n} sessão(ões) de pacote registada(s), além da venda.`);
            setExchange(null);
            setLast(doc);
            p.onSold();
            issued(doc);
            void printThermal(receiptHtml(doc, p.device, { staffNames }), { drawer: doc.payments.some((x) => x.method === 'NU') });
            search.current?.focus();
          }}
        />
      )}
    </div>
  );
}

function Chip(p: { active: boolean; color?: string | null; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={p.onClick}
      style={p.active && p.color ? { background: p.color, borderColor: p.color } : undefined}
      className={clsx('h-12 shrink-0 rounded-full border px-4 font-medium', p.active ? 'border-brand-700 bg-brand-700 text-white' : 'border-base bg-[var(--surface)]')}
    >{p.children}</button>
  );
}
